import { Button, ResourceOptions, type SortConfig, TableHead } from "@ondit/ui";
import { ArrowDown, ArrowUp, Download } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useT } from "~/core/i18n";

/**
 * The furniture every table in the panel shares: find a row, sort by a column,
 * take the numbers away.
 *
 * All three are client-side on purpose. These tables are capped at fifty rows
 * by the endpoints that serve them, so a round trip to filter them would be
 * slower and no more correct — and the download has to be exactly what is on
 * screen, filters and sort included, or it is a different answer to a different
 * question.
 */
export function TableTools({
	query,
	onQuery,
	placeholder,
	shown,
	total,
	csv,
	children,
	actions,
	sort,
}: {
	query: string;
	onQuery: (next: string) => void;
	placeholder?: string;
	shown: number;
	total: number;
	/** Header row first. Omit to leave the download button off. */
	csv?: { name: string; rows: (string | number)[][] };
	children?: ReactNode;
	actions?: ReactNode;
	sort?: SortConfig;
}) {
	const t = useT();
	const field = useRef<HTMLInputElement>(null);
	const filtered = shown !== total;

	// `/` puts the cursor in the search from anywhere on the screen, the way it
	// does in every tool these customers already have open. Guarded on whatever
	// has focus: somebody typing a question into the form above the table is
	// typing a slash, not reaching for a shortcut.
	useEffect(() => {
		function onKey(event: KeyboardEvent) {
			if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
			if (document.querySelector("[role=dialog], [role=alertdialog], [role=menu], [role=listbox]")) return;
			const active = document.activeElement;
			if (
				active instanceof HTMLElement &&
				(active.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName))
			)
				return;
			event.preventDefault();
			field.current?.focus();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, []);

	return (
		<ResourceOptions
			translate={t}
			className="analytics-table-tools"
			search={{
				value: query,
				onChange: onQuery,
				placeholder: placeholder ?? t("table.search"),
				inputRef: field,
				onKeyDown: (event) => {
					if (event.key !== "Escape") return;
					event.preventDefault();
					if (query) onQuery("");
					else event.currentTarget.blur();
				},
			}}
			sort={sort}
			aside={
				<span className="faint num">
					{filtered ? t("table.showing", { shown, total }) : t("table.rows", { total })}
				</span>
			}
			asideEnd={
				<>
					{children}
					{actions}
					{csv ? (
						<Button
							variant="ghost"
							type="button"
							disabled={shown === 0}
							onClick={() => download(csv.name, csv.rows)}
							title={t("table.export")}
						>
							<Download aria-hidden />
							{t("table.export")}
						</Button>
					) : null}
				</>
			}
		/>
	);
}

/**
 * A sortable column heading.
 *
 * The arrow only appears on the column actually being sorted; three columns
 * each showing a direction is three claims about the order of the table, and
 * only one of them is true.
 */
export function SortHeader<K extends string>({
	label,
	column,
	sort,
	className,
}: {
	label: string;
	column: K;
	sort: Sort<K>;
	className?: string;
}) {
	const active = sort.by === column;
	return (
		<TableHead className={className} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
			<Button
				variant="ghost"
				className="th-sort h-auto min-h-(--control-h) w-full justify-start whitespace-nowrap px-0 text-left [.num_&]:justify-end"
				type="button"
				onClick={() => sort.toggle(column)}
			>
				{label}
				{active ? sort.dir === "asc" ? <ArrowUp aria-hidden /> : <ArrowDown aria-hidden /> : null}
			</Button>
		</TableHead>
	);
}

export interface Sort<K extends string> {
	by: K;
	dir: "asc" | "desc";
	config: (options: { id: K; label: string }[]) => SortConfig;
	toggle: (column: K) => void;
	/** Sorts a copy. The caller's array is somebody else's state. */
	apply: <T>(rows: T[], value: (row: T, column: K) => string | number | null) => T[];
}

/**
 * Sort state for one table.
 *
 * The first click on a new column sorts descending, because every column in
 * this panel is a count or a share and the interesting end of those is the top.
 */
export function useSort<K extends string>(initial: K, initialDir: "asc" | "desc" = "desc"): Sort<K> {
	const [by, setBy] = useState<K>(initial);
	const [dir, setDir] = useState<"asc" | "desc">(initialDir);

	const toggle = useCallback(
		(column: K) => {
			if (column === by) {
				setDir((d) => (d === "asc" ? "desc" : "asc"));
			} else {
				setBy(column);
				setDir("desc");
			}
		},
		[by],
	);

	return useMemo(
		() => ({
			by,
			dir,
			toggle,
			config: (options) => ({
				options,
				active: { column: by, direction: dir },
				onSort: (column, direction) => {
					if (!options.some((option) => option.id === column)) return;
					setBy(column as K);
					setDir(direction);
				},
				onClear: () => {
					setBy(initial);
					setDir(initialDir);
				},
			}),
			apply: (rows, value) =>
				[...rows].sort((a, b) => {
					const left = value(a, by);
					const right = value(b, by);
					// A missing value is never "smallest": it means we have not
					// measured, and sorting it against a measurement would rank an
					// unknown above a real zero.
					if (left === null) return 1;
					if (right === null) return -1;
					const order =
						typeof left === "number" && typeof right === "number"
							? left - right
							: String(left).localeCompare(String(right));
					return dir === "asc" ? order : -order;
				}),
		}),
		[by, dir, toggle, initial, initialDir],
	);
}

/** Case-insensitive substring match over the fields a row can be found by. */
export function matches(query: string, ...fields: (string | null | undefined)[]): boolean {
	const needle = query.trim().toLowerCase();
	if (!needle) return true;
	return fields.some((field) => field?.toLowerCase().includes(needle));
}

/**
 * A CSV of what is on screen.
 *
 * The byte-order mark is not decoration: without it Excel on Windows reads a
 * UTF-8 file as the local code page, and every Chinese question in the export
 * arrives as mojibake.
 *
 * Exported because a chart is also "what is on screen": the insight tabs put a
 * download beside every metric block, and a second implementation of this would
 * be a second file that Excel reads as the local code page.
 */
export function download(name: string, rows: (string | number)[][]): void {
	const body = rows.map((row) => row.map((cell) => `"${csvCell(cell).replaceAll('"', '""')}"`).join(",")).join("\r\n");
	const blob = new Blob([`﻿${body}`], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `${name}.csv`;
	link.click();
	URL.revokeObjectURL(url);
}

/**
 * A cell a spreadsheet will show rather than run.
 *
 * Excel and its relatives treat a cell beginning with `=`, `+`, `-` or `@` as
 * a formula, and quoting does not stop them. Much of what is exported here —
 * a rival's page title, a URL an answer engine returned — was written by
 * somebody else, so a title of `=HYPERLINK(...)` would execute on the
 * customer's machine when they opened the file. The leading apostrophe is the
 * spreadsheet convention for "this is text", and the whitespace characters are
 * covered because Excel skips them before deciding.
 */
export function csvCell(cell: string | number): string {
	const text = String(cell);
	return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}
